import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Search, Upload, GitBranch, ExternalLink, FileText, Github } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import DeployModal from './DeployModal';

interface OpenAPIFile {
  name: string;
  path: string;
  repository: string;
  size: number;
  lastModified: string;
}

interface GitHubRepository {
  name: string;
  fullName: string;
  description?: string;
  updatedAt: string;
  isPrivate: boolean;
}

interface DeployData {
  projectName: string;
  path: string;
  branchName: string;
  installationId: string;
  githubAppName: string;
  githubAppId: string;
}

interface DeployedProject {
  id: string;
  MIAproject: string;
  projectName: string;
  path: string;
  apiVersion: string;
  deployedAt: string;
  lastUpdated: string;
  status: 'success' | 'deployed' | 'building' | 'error';
  source: 'github' | 'thirdparty' | 'local';
  githubUrl?: string;
  apiDevPortal?: string;
}

interface ProjectSelection {
  projectName: string;
  path: string;
  apiVersion: string;
  MIAproject: string;
  githubUrl?: string;
  source: 'github' | 'thirdparty' | 'local';
}

interface OpenAPISelectorProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onProjectSelected: (project: ProjectSelection) => void;
  githubHandle: string;
  branchName: string;
  installationId: string;
  githubAppName: string;
  githubAppId: string;
  // New prop to notify parent about successful deployment
  onProjectDeployed?: (project: DeployedProject) => void;
}

// Cache management class (same as in DeployedProjectTable)
class DeployedProjectsCache {
  private static instance: DeployedProjectsCache;
  private cache: DeployedProject[] | null = null;
  private lastFetch: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
  private listeners: Set<() => void> = new Set();

  static getInstance(): DeployedProjectsCache {
    if (!DeployedProjectsCache.instance) {
      DeployedProjectsCache.instance = new DeployedProjectsCache();
    }
    return DeployedProjectsCache.instance;
  }

  getCachedProjects(): DeployedProject[] | null {
    const now = Date.now();
    if (this.cache && (now - this.lastFetch) < this.CACHE_DURATION) {
      return this.cache;
    }
    return null;
  }

  setCachedProjects(projects: DeployedProject[]): void {
    this.cache = projects;
    this.lastFetch = Date.now();
    this.notifyListeners();
  }

  invalidateCache(): void {
    this.cache = null;
    this.lastFetch = 0;
    this.notifyListeners();
  }

  addProject(project: DeployedProject): void {
    if (this.cache) {
      this.cache.unshift(project);
      this.notifyListeners();
    }
  }

  removeProject(projectId: string): void {
    if (this.cache) {
      this.cache = this.cache.filter(p => p.id !== projectId);
      this.notifyListeners();
    }
  }

  updateProject(projectId: string, updates: Partial<DeployedProject>): void {
    if (this.cache) {
      const index = this.cache.findIndex(p => p.id === projectId);
      if (index !== -1) {
        this.cache[index] = { ...this.cache[index], ...updates };
        this.notifyListeners();
      }
    }
  }

  // Listener management for real-time updates
  addListener(listener: () => void): void {
    this.listeners.add(listener);
  }

  removeListener(listener: () => void): void {
    this.listeners.delete(listener);
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }
}

const OpenAPISelector: React.FC<OpenAPISelectorProps> = ({
  isOpen,
  onOpenChange,
  onProjectSelected,
  githubHandle,
  branchName,
  installationId,
  githubAppName,
  githubAppId,
  onProjectDeployed
}) => {
  const [activeTab, setActiveTab] = useState<'github' | 'thirdparty' | 'local'>('github');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [repositories, setRepositories] = useState<GitHubRepository[]>([]);
  const [selectedRepo, setSelectedRepo] = useState<GitHubRepository | null>(null);
  const [repoFiles, setRepoFiles] = useState<OpenAPIFile[]>([]);
  const [thirdPartyUrl, setThirdPartyUrl] = useState('');
  const [localFile, setLocalFile] = useState<File | null>(null);
  const [localFileContent, setLocalFileContent] = useState<string>('');
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [deployData, setDeployData] = useState<DeployData | null>(null);
  const { toast } = useToast();

  const cache = DeployedProjectsCache.getInstance();

  // Fetch GitHub repositories
  const fetchRepositories = async () => {
    setIsLoading(true);
    try {
      const response = await fetch('/api/github-repositories');
      if (!response.ok) throw new Error('Failed to fetch repositories');
      
      const data = await response.json();
      setRepositories(data.repositories || []);
    } catch (error) {
      console.error('Error fetching repositories:', error);
      toast({
        title: "Error",
        description: "Failed to fetch GitHub repositories",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }; 

  // Fetch files from selected repository
  const fetchRepoFiles = async (repo: GitHubRepository) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/api/files?repo=${encodeURIComponent(repo.name)}`);
      if (!response.ok) throw new Error('Failed to fetch repository files');
      
      const data = await response.json();
      const openApiFiles = data.files
        .filter((file: any) => 
          file.name.match(/\.(yaml|yml|json)$/i) &&
          (file.name.toLowerCase().includes('openapi') || 
           file.name.toLowerCase().includes('swagger') ||
           file.path.toLowerCase().includes('api'))
        )
        .map((file: any) => ({
          name: file.name,
          path: file.path,
          repository: repo.name,
          size: file.size || 0,
          lastModified: new Date().toISOString()
        }));
      
      setRepoFiles(openApiFiles);
    } catch (error) {
      console.error('Error fetching repository files:', error);
      toast({
        title: "Error",
        description: "Failed to fetch repository files",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Load repositories when GitHub tab is selected
  useEffect(() => {
    if (isOpen && activeTab === 'github' && repositories.length === 0) {
      fetchRepositories();
    }
  }, [isOpen, activeTab]);

  // Filter repositories based on search
  const filteredRepositories = repositories.filter(repo =>
    repo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    repo.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle GitHub file selection - now opens deploy modal
  const handleGitHubFileSelect = async (file: OpenAPIFile) => {
    try {
      // Set up deploy data for the modal
      setDeployData({
        projectName: file.repository,
        path: file.path,
        branchName,
        installationId,
        githubAppName,
        githubAppId
      });
      
      // Open the deploy modal
      setIsDeployModalOpen(true);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to process selected file",
        variant: "destructive",
      });
    }
  };

  // Handle successful deployment from modal
  const handleDeploySuccess = async () => {
    if (!deployData) return;

    // Create the deployed project object
    const newProject: DeployedProject = {
      id: `${deployData.projectName}-${deployData.path}-${Date.now()}`,
      MIAproject: generateDefaultProjectName(deployData.projectName),
      projectName: deployData.projectName,
      path: deployData.path,
      apiVersion: '1.0.0', // Default, will be updated when status is checked
      deployedAt: new Date().toISOString(),
      lastUpdated: new Date().toISOString(),
      status: 'building',
      source: 'github', // Add the missing source property
      githubUrl: `https://github.com/${githubHandle}/${deployData.projectName}/blob/${branchName}/${deployData.path}`,
      apiDevPortal: undefined // Will be set when deployment completes
    };

    // Add to cache immediately
    cache.addProject(newProject);

    // Notify parent component about the new deployment
    if (onProjectDeployed) {
      onProjectDeployed(newProject);
    }

    // Close modals and show success
    setIsDeployModalOpen(false);
    onOpenChange(false);
    
    toast({
      title: "Success",
      description: "API deployment started successfully",
    });
  };

  // Handle third-party URL selection
  const handleThirdPartySelect = () => {
    if (!thirdPartyUrl.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid URL",
        variant: "destructive",
      });
      return;
    }

    try {
      const url = new URL(thirdPartyUrl);
      const projectName = url.pathname.split('/').pop()?.replace(/\.(yaml|yml|json)$/i, '') || 'external-api';
      
      onProjectSelected({
        projectName,
        path: thirdPartyUrl,
        apiVersion: '1.0.0',
        MIAproject: generateDefaultProjectName(projectName),
        source: 'thirdparty'
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Please enter a valid URL",
        variant: "destructive",
      });
    }
  };

  // Handle local file selection
  const handleLocalFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.match(/\.(yaml|yml|json)$/i)) {
      toast({
        title: "Error",
        description: "Please select a YAML or JSON file",
        variant: "destructive",
      });
      return;
    }

    setLocalFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      setLocalFileContent(content);
    };
    reader.readAsText(file);
  };

  // Handle local file deployment
  const handleLocalFileDeployment = () => {
    if (!localFile || !localFileContent) {
      toast({
        title: "Error",
        description: "Please select a file first",
        variant: "destructive",
      });
      return;
    }

    const projectName = localFile.name.replace(/\.(yaml|yml|json)$/i, '');
    
    onProjectSelected({
      projectName,
      path: localFile.name,
      apiVersion: '1.0.0',
      MIAproject: generateDefaultProjectName(projectName),
      source: 'local'
    });
  };

  // Generate default project name
  const generateDefaultProjectName = (baseName: string) => {
    const sanitized = baseName.replace(/[^a-zA-Z0-9]/g, '');
    const randomSuffix = Math.random().toString(36).substring(2, 5);
    return `${sanitized}${randomSuffix}`;
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedRepo(null);
      setRepoFiles([]);
      setThirdPartyUrl('');
      setLocalFile(null);
      setLocalFileContent('');
      setSearchTerm('');
    }
  }, [isOpen]);

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[80vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Import OpenAPI Specification
            </DialogTitle>
          </DialogHeader>
          
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="github" className="flex items-center gap-2">
                <Github className="h-4 w-4" />
                GitHub Repositories
              </TabsTrigger>
              <TabsTrigger value="thirdparty" className="flex items-center gap-2">
                <ExternalLink className="h-4 w-4" />
                Third-Party Repository
              </TabsTrigger>
              <TabsTrigger value="local" className="flex items-center gap-2">
                <Upload className="h-4 w-4" />
                Local Files
              </TabsTrigger>
            </TabsList>

            {/* GitHub Tab */}
            <TabsContent value="github" className="mt-4 space-y-4">
              {!selectedRepo ? (
                <div className="space-y-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search repositories..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto border rounded-md">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        <span>Loading repositories...</span>
                      </div>
                    ) : filteredRepositories.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No repositories found
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Repository</TableHead>
                            <TableHead>Updated</TableHead>
                            <TableHead style={{ width: 100 }}>Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredRepositories.map((repo) => (
                            <TableRow key={repo.fullName}>
                              <TableCell>
                                <div className="flex flex-col">
                                  <span className="font-medium">{repo.name}</span>
                                  {repo.description && (
                                    <span className="text-sm text-gray-500">{repo.description}</span>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">
                                {new Date(repo.updatedAt).toLocaleDateString()}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    setSelectedRepo(repo);
                                    fetchRepoFiles(repo);
                                  }}
                                >
                                  Browse
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedRepo(null)}
                    >
                      ← Back
                    </Button>
                    <span className="font-medium">{selectedRepo.name}</span>
                  </div>
                  
                  <div className="max-h-96 overflow-y-auto border rounded-md">
                    {isLoading ? (
                      <div className="flex items-center justify-center py-8">
                        <Loader2 className="h-6 w-6 animate-spin mr-2" />
                        <span>Loading files...</span>
                      </div>
                    ) : repoFiles.length === 0 ? (
                      <div className="text-center py-8 text-gray-500">
                        No OpenAPI files found in this repository
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>File</TableHead>
                            <TableHead>Path</TableHead>
                            <TableHead style={{ width: 100 }}>Action</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {repoFiles.map((file, index) => (
                            <TableRow key={index}>
                              <TableCell>
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-blue-500" />
                                  <span className="font-medium">{file.name}</span>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm text-gray-500">
                                {file.path}
                              </TableCell>
                              <TableCell>
                                <Button
                                  size="sm"
                                  onClick={() => handleGitHubFileSelect(file)}
                                >
                                  Deploy
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>

            {/* Third-Party Tab */}
            <TabsContent value="thirdparty" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Import from URL</CardTitle>
                  <CardDescription>
                    Enter the URL of an OpenAPI specification file (YAML or JSON)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="url">Git Repository URL</Label>
                    <Input
                      id="url"
                      placeholder="https://github.com/scope/repo/.../path/to/openapi.yaml"
                      value={thirdPartyUrl}
                      onChange={(e) => setThirdPartyUrl(e.target.value)}
                    />
                  </div>
                  <Button 
                    onClick={handleThirdPartySelect}
                    disabled={!thirdPartyUrl.trim()}
                    className="w-full"
                  >
                    Continue
                  </Button>
                </CardContent>
              </Card>
            </TabsContent>

            {/* Local Files Tab */}
            <TabsContent value="local" className="mt-4">
              <Card>
                <CardHeader>
                  <CardTitle>Upload Local File</CardTitle>
                  <CardDescription>
                    Upload an OpenAPI specification file from your computer (YAML or JSON)
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="file">Choose File</Label>
                    <Input
                      id="file"
                      type="file"
                      accept=".yaml,.yml,.json"
                      onChange={handleLocalFileSelect}
                    />
                  </div>
                  
                  {localFile && (
                    <div className="p-4 bg-gray-50 rounded-md">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="h-4 w-4 text-blue-500" />
                        <span className="font-medium">{localFile.name}</span>
                        <span className="text-sm text-gray-500">
                          ({Math.round(localFile.size / 1024)} KB)
                        </span>
                      </div>
                      <Button 
                        onClick={handleLocalFileDeployment}
                        className="w-full"
                      >
                        Deploy from Local File
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Deploy Modal */}
      {deployData && (
        <DeployModal
          isOpen={isDeployModalOpen}
          onOpenChange={setIsDeployModalOpen}
          deployData={deployData}
          onSuccess={handleDeploySuccess}
        />
      )}
    </>
  );
};

export default OpenAPISelector;